// TeamsPage.jsx
import { useState, useEffect, useMemo } from "react";
import { RefreshCcw, MapPin } from "lucide-react";
import { Toaster, toast } from "sonner";
import TeamTable from "./TeamTable";
import LoadingScreen from "../Utils/LoadingScreen";
import { useGlobalContext } from "../../GlobalContext";
import {
  CreatePincodeTeamModal,
  AssignShopsModal,
  extractPincodeOptions,
  getPincodeId,
  getPincodeLabel,
  getPincodeAddress,
} from "./TeamsModals";

// Reuse your existing modals
import SendNotificationModal from "../Driver/SendNotificationModal";
import BulkSendNotificationModal from "../Driver/BulkSendNotificationModal";
import TablePagination from "../Utils/TablePagination";
import useTeams from "../../Sevices/useTeams";
import useShop from "../../Sevices/useShop";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";
import useDriver from "../../Sevices/Driver Api/useDriver";

export default function TeamsPage() {
  const { loading, getPincodeDetails, createDriverTeamV2, assignShopToTeam } =
    useTeams();

  const { toggleDutyStatus } = useDriver();

  const {
    teams,
    fetchDriverTeams,
    teamMembersByTeamId,
    teamMembersLoadingId,
    fetchDriverTeamListByTeamId,
  } = useDriverTeams();

  const { baseurl, token, accessToken, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const { shops, fetchShops } = useShop({
    baseurl,
    accessToken,
    token,
    AdminId,
    setIsUnauthorised,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Shops (assign one-by-one)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeam, setAssignTeam] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignWorkingCode, setAssignWorkingCode] = useState(""); // show spinner per-row

  // Notifications
  const [singleNotifOpen, setSingleNotifOpen] = useState(false);
  const [singleNotifDriver, setSingleNotifDriver] = useState(null);
  const [bulkNotifOpen, setBulkNotifOpen] = useState(false);
  const [bulkDrivers, setBulkDrivers] = useState([]);

  // Duty toggle (local state cache)
  const [dutyWorkingId, setDutyWorkingId] = useState(null);
  const [dutyMap, setDutyMap] = useState(new Map()); // driverId -> "on_duty"/"off_duty"

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [accuracy, setAccuracy] = useState("");

  // New V2 pincode/area team flow
  const [showCreatePincodeModal, setShowCreatePincodeModal] = useState(false);
  const [pincodeTeamForm, setPincodeTeamForm] = useState({
    pinCode: "",
    address: "",
    teamName: "",
    minimumDrivers: "",
    maximumDrivers: "",
    accuracy: "",
  });
  const [pincodeSearch, setPincodeSearch] = useState("");
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [pincodeSearchLoading, setPincodeSearchLoading] = useState(false);
  const [pincodeDropdownOpen, setPincodeDropdownOpen] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [openAssignDropdownTeamId, setOpenAssignDropdownTeamId] =
    useState(null);

  const teamsPerPage = 15;

  const filteredTeams = useMemo(() => {
    if (!Array.isArray(teams)) return [];

    return teams.filter(
      (team) =>
        (!teamStatusFilter || team.status === teamStatusFilter) &&
        (search === "" ||
          team.team_name?.toLowerCase().includes(search.toLowerCase()) ||
          team.teamName?.toLowerCase().includes(search.toLowerCase()) ||
          team.pinCode?.toLowerCase().includes(search.toLowerCase()) ||
          team.pin_code?.toLowerCase().includes(search.toLowerCase()) ||
          team.pincode?.toLowerCase().includes(search.toLowerCase()) ||
          team.address?.toLowerCase().includes(search.toLowerCase()) ||
          team.team_address?.toLowerCase().includes(search.toLowerCase()) ||
          team.createdOn?.toLowerCase().includes(search.toLowerCase())),
    );
  }, [teams, teamStatusFilter, search]);

  const indexOfLast = currentPage * teamsPerPage;
  const indexOfFirst = indexOfLast - teamsPerPage;
  const currentTeams = filteredTeams.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    fetchDriverTeams();
    fetchShops();
  }, []);

  useEffect(() => {
    if (!showCreatePincodeModal) return;

    const word = pincodeSearch.trim();

    if (!word) {
      setPincodeOptions([]);
      setPincodeDropdownOpen(false);
      return;
    }

    if (
      selectedPincode &&
      getPincodeLabel(selectedPincode) === word &&
      pincodeTeamForm.pinCode
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      setPincodeSearchLoading(true);

      try {
        const data = await getPincodeDetails({
          word,
          pageSize: 20,
          nextToken: "",
          previousToken: "",
        });

        const options = extractPincodeOptions(data);
        setPincodeOptions(options);
        setPincodeDropdownOpen(true);
      } finally {
        setPincodeSearchLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [
    pincodeSearch,
    showCreatePincodeModal,
    selectedPincode,
    pincodeTeamForm.pinCode,
  ]);

  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);

    try {
      await fetchDriverTeams(true);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePincodeTeamChange = (field, value) => {
    setPincodeTeamForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetPincodeTeamForm = () => {
    setPincodeTeamForm({
      pinCode: "",
      address: "",
      teamName: "",
      minimumDrivers: "",
      maximumDrivers: "",
      accuracy: "",
    });
    setPincodeSearch("");
    setPincodeOptions([]);
    setPincodeDropdownOpen(false);
    setSelectedPincode(null);
  };

  const handlePincodeSearchChange = (value) => {
    const numericValue = value.replace(/\D/g, "");

    setPincodeSearch(numericValue);
    setSelectedPincode(null);
    setPincodeDropdownOpen(Boolean(numericValue));
    handlePincodeTeamChange("pinCode", "");
  };

  const handleSelectPincode = (item) => {
    const pincode = getPincodeLabel(item);

    if (!pincode) {
      toast.error("Pincode not found");
      return;
    }

    setSelectedPincode(item);
    setPincodeSearch(pincode);
    setPincodeOptions([]);
    setPincodeDropdownOpen(false);

    const pincodeId = getPincodeId(item);

    if (!pincodeId) {
      toast.error("Pincode ID not found");
      return;
    }

    // createDriverTeam V2 expects the selected pincode record ID.
    // Auto-fill the selected pincode address, but keep the field editable.
    const selectedAddress = getPincodeAddress(item);

    setPincodeTeamForm((prev) => ({
      ...prev,
      pinCode: String(pincodeId),
      address: selectedAddress || prev.address,
    }));
  };

  const handleCreatePincodeTeam = async () => {
    if (!selectedPincode || !pincodeTeamForm.pinCode) {
      toast.error("Please select a pincode from the list");
      return;
    }

    await createDriverTeamV2({
      ...pincodeTeamForm,
      onSuccess: async () => {
        setShowCreatePincodeModal(false);
        resetPincodeTeamForm();
        await fetchDriverTeams(true);
      },
    });
  };

  const toggleExpand = async (id) => {
    const isAlreadyOpen = expanded.has(id);

    if (!isAlreadyOpen) {
      await fetchDriverTeamListByTeamId(id);
    }

    setExpanded((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  };

  // ---------- Notifications ----------
  const openSingleNotification = (driver, team) => {
    // Map team driver shape -> modal expected shape
    const mapped = {
      driverId: driver.driver_code || driver.id, // use driver_code as ID
      fullName:
        driver.fullName ||
        `${driver.first_name || ""} ${driver.last_name || ""}`.trim(),
      username: driver.username || "",
      status: driver.status || "",
      teamName: team?.team_name || "",
    };
    setSingleNotifDriver(mapped);
    setSingleNotifOpen(true);
  };

  const openBulkNotification = async (team) => {
    let memberData = teamMembersByTeamId[team.id];

    if (!memberData) {
      memberData = await fetchDriverTeamListByTeamId(team.id);
    }

    const combinedMembers = [
      ...(memberData?.primaryTeamDrivers || []),
      ...(memberData?.secondaryTeamDrivers || []),
    ];

    const mapped = combinedMembers.map((d) => ({
      driverId: d.driver_code || d.id,
      fullName:
        d.fullName || `${d.first_name || ""} ${d.last_name || ""}`.trim(),
      username: d.username || "",
      status: d.driverStatus || d.status || "",
      teamName: team.team_name,
    }));

    setBulkDrivers(mapped);
    setBulkNotifOpen(true);
  };

  // ---------- Duty toggle (from DriversPage logic) ----------
  const handleToggleDutyStatus = async (driverCode) => {
    const current = dutyMap.get(driverCode) || "off_duty";
    const next = current === "on_duty" ? "off_duty" : "on_duty";

    setDutyWorkingId(driverCode);
    setDutyMap((prev) => new Map(prev).set(driverCode, next));

    await toggleDutyStatus({ driverId: driverCode }, current === "on_duty");
    setDutyWorkingId(null);
  };

  // ---------- Assign Shops (single per call) ----------
  const openAssignShops = async (team) => {
    setAssignTeam(team);
    if (shops.length === 0) await fetchShops();
    setAssignSearch("");
    setAssignOpen(true);
  };

  const assignOneShop = async (shop) => {
    if (!assignTeam) return;

    const code = shop.shop_code;

    const curTeamName = (shop.teamName || "").trim();

    const isElsewhere =
      curTeamName &&
      curTeamName.toLowerCase() !== (assignTeam.team_name || "").toLowerCase();

    if (isElsewhere) {
      toast.error(`Already assigned to ${curTeamName}`);
      return;
    }

    const alreadyInThisTeam = (assignTeam.shopList || []).some(
      (s) => s.shop_code === code,
    );

    if (alreadyInThisTeam) {
      toast.info("This shop is already mapped to the same team.");
      return;
    }

    setAssignWorkingCode(code);

    try {
      await assignShopToTeam({
        shopCode: shop.shop_code,
        teamId: assignTeam.id,
        onSuccess: async () => {
          await fetchDriverTeams(true);

          setAssignTeam((prev) =>
            prev
              ? {
                  ...prev,
                  shopList: [
                    ...(prev.shopList || []),
                    {
                      id: shop.id,
                      shop_code: shop.shop_code,
                      shop_name: shop.shop_name,
                      shop_address: shop.shop_address,
                      shop_phone: shop.shop_phone,
                      onboardedOn: shop.onboardedOn,
                      areaid: shop.areaid,
                    },
                  ],
                }
              : prev,
          );
        },
      });
    } finally {
      setAssignWorkingCode("");
    }
  };

  const assignShopFromDropdown = async (team, shopCode) => {
    if (!team || !shopCode) return;

    const shop = shops.find((s) => String(s.shop_code) === String(shopCode));
    if (!shop) {
      toast.error("Shop not found");
      return;
    }

    setAssignWorkingCode(shop.shop_code);

    try {
      await assignShopToTeam({
        shopCode: shop.shop_code,
        teamId: team.id,
        onSuccess: async () => {
          setOpenAssignDropdownTeamId(null);
          await fetchDriverTeams(true);
        },
      });
    } finally {
      setAssignWorkingCode("");
    }
  };

  const filteredShopsForAssign = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    const list = shops || [];
    if (!q) return list;
    return list.filter(
      (s) =>
        s.shop_name?.toLowerCase().includes(q) ||
        s.shop_code?.toLowerCase().includes(q) ||
        s.shop_address?.toLowerCase().includes(q),
    );
  }, [shops, assignSearch]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(filteredTeams.length / teamsPerPage));

    if (currentPage > total) {
      setCurrentPage(total);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [filteredTeams.length, teamsPerPage, currentPage]);

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      <Toaster position="top-right" />
      <div className="sticky top-0 z-30 pb-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">Teams</h1>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          {/*  Search & Filters (left) */}
          <div className="flex flex-wrap items-end gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team, pincode, address or date..."
              className="px-2 py-1 border rounded text-xs shadow-sm w-[180px]"
            />
            <select
              value={teamStatusFilter}
              onChange={(e) => setTeamStatusFilter(e.target.value)}
              className="text-xs border rounded px-2 py-1 shadow-sm w-[130px]"
            >
              <option value="">All Status</option>
              <option value="publish">Publish</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/*  Actions (right) */}
          <div className="flex flex-wrap items-end gap-2">
            <button
              className="flex items-center space-x-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() => setShowCreatePincodeModal(true)}
            >
              <MapPin size={14} />
              <span>Create Pincode Team</span>
            </button>

            <button
              className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCcw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>
      </div>

      {teams.length === 0 ? (
        <LoadingScreen />
      ) : (
        <>
          <div className="flex-1 min-h-0 border rounded-md shadow-sm overflow-hidden">
            <div className="h-full overflow-y-auto overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#2EC8F1] text-left text-sm font-semibold text-white">
                    <th className="bg-[#2EC8F1] px-3 py-3 w-14 text-center">
                      #
                    </th>
                    <th className="bg-[#2EC8F1] px-4 py-3">Team</th>
                    <th className="bg-[#2EC8F1] px-4 py-3">
                      Coverage / Rules
                    </th>
                    <th className="bg-[#2EC8F1] px-4 py-3">Members / Shops</th>
                    <th className="bg-[#2EC8F1] px-4 py-3">Status</th>
                    <th className="bg-[#2EC8F1] px-4 py-3 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentTeams.map((team) => {
                    const isOpen = expanded.has(team.id);
                    const memberData = teamMembersByTeamId[team.id] || {
                      primaryTeamDrivers: [],
                      secondaryTeamDrivers: [],
                      activeTeamDrivers: [],
                    };

                    const members = [
                      ...(memberData.primaryTeamDrivers || []).map((d) => ({
                        ...d,
                        memberType: "primary",
                      })),
                      ...(memberData.secondaryTeamDrivers || []).map((d) => ({
                        ...d,
                        memberType: "secondary",
                      })),
                    ];

                    const activeDrivers = memberData.activeTeamDrivers || [];
                    const activeDriverCodeSet = new Set(
                      activeDrivers.map((d) => d.driver_code).filter(Boolean),
                    );
                    const shopsMapped = Array.isArray(team.shopList)
                      ? team.shopList
                      : [];

                    return (
                      <TeamTable
                        key={team.id}
                        team={team}
                        isOpen={isOpen}
                        members={members}
                        activeDrivers={activeDrivers}
                        activeDriverCodeSet={activeDriverCodeSet}
                        memberLoading={teamMembersLoadingId === team.id}
                        shops={shopsMapped}
                        allShops={shops}
                        openAssignDropdownTeamId={openAssignDropdownTeamId}
                        assignWorkingCode={assignWorkingCode}
                        dutyMap={dutyMap}
                        dutyWorkingId={dutyWorkingId}
                        onToggleDuty={handleToggleDutyStatus}
                        onToggle={() => toggleExpand(team.id)}
                        onOpenAssign={() => openAssignShops(team)}
                        onToggleAssignDropdown={() => {
                          setOpenAssignDropdownTeamId((prev) =>
                            prev === team.id ? null : team.id,
                          );
                        }}
                        onAssignShopFromDropdown={(shopCode) =>
                          assignShopFromDropdown(team, shopCode)
                        }
                        onOpenSingleNotif={(driver) =>
                          openSingleNotification(driver, team)
                        }
                        onOpenBulkNotif={() => openBulkNotification(team)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalCount={filteredTeams.length}
            pageSize={teamsPerPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ============================================================
    CREATE PINCODE TEAM MODAL
============================================================ */}
      <CreatePincodeTeamModal
        open={showCreatePincodeModal}
        loading={loading}
        form={pincodeTeamForm}
        search={pincodeSearch}
        options={pincodeOptions}
        searchLoading={pincodeSearchLoading}
        dropdownOpen={pincodeDropdownOpen}
        selectedPincode={selectedPincode}
        onChange={handlePincodeTeamChange}
        onSearchChange={handlePincodeSearchChange}
        onSearchFocus={() => setPincodeDropdownOpen(true)}
        onSelectPincode={handleSelectPincode}
        onCreate={handleCreatePincodeTeam}
        onClose={() => {
          setShowCreatePincodeModal(false);
          resetPincodeTeamForm();
        }}
      />

      {/* ============================================================
    ASSIGN SHOPS MODAL
============================================================ */}
      <AssignShopsModal
        open={assignOpen}
        team={assignTeam}
        search={assignSearch}
        shops={filteredShopsForAssign}
        workingCode={assignWorkingCode}
        onSearchChange={setAssignSearch}
        onAssign={assignOneShop}
        onClose={() => setAssignOpen(false)}
      />

      {/* Notification Modals */}
      <SendNotificationModal
        isOpen={singleNotifOpen}
        onClose={() => setSingleNotifOpen(false)}
        driver={singleNotifDriver}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
      />

      <BulkSendNotificationModal
        isOpen={bulkNotifOpen}
        onClose={() => setBulkNotifOpen(false)}
        drivers={bulkDrivers}
        teams={teams}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
        defaultTeam=""
        defaultStatus=""
      />
    </div>
  );
}
